import { defineComponent as _defineComponent } from 'vue'
import {
  useI18n,
} from "@/composables/useI18n";

import {
  computed,
  onMounted,
  ref,
  unref,
  watch,
} from "vue";

import type {
  AccountingPartner,
} from "@/components/customers/scripts";

import {
  Issuer,
} from "@/model/Issuer";

import {
  getAccessRights,
} from "@/authorization";

import {
  accountDataPolicies,
} from "@/authorization/policies";

import FloatingActionButton from "@/components/buttons/FloatingActionButton.vue";

import {
  cardExist,
} from "@/components/cards/scripts";

import {
  debounce,
} from "@/scripts/Common/HelperFunctions";

import {
  fetchAccountingPartners,
} from "@/components/customers/scripts";

import {
  fetchIssuers,
} from "@/components/issuers/scripts/issuers";

import {
  FetchIssuersParam
} from "@/components/issuers/scripts/issuers/types";

import {
  resetPassword,
} from "@/components/administrations/scripts/passwordReset";

import {
  logError,
} from "@/scripts/Common";

import InfoSnackbar from "@/components/children/InfoSnackbar.vue";

import {
  useSnackbar,
} from "@/composables/useSnackbar";

import {
  DEFAULT_SORT_COLUMN_FETCH_ISSUERS
} from  "@/components/issuers/scripts/constants";


export default /*#__PURE__*/_defineComponent({
  __name: 'PasswordReset',
  emits: [
  'onFetchError'
],
  setup(__props, { emit: emits }) {


const {
  t,
} = useI18n();

const {
  hasEditRights,
} = getAccessRights(accountDataPolicies);



const issuerComboSelection = ref<Issuer | undefined>(undefined);
const issuerSearchInput = ref<string | undefined>(undefined);
let ignoreIssuerSearchInputChange = false; // when the issuer is selected from the combobox, the issuerSearchInput change that follows should not trigger a reload of the issuer list


const tabs = ref(null);
const vendorForm = ref();
const userForm = ref();
const issuerForm = ref();

const isValidationBusy = ref(false);

const vendorValid = ref(false);
const issuerValid = ref(false);
const userValid = ref(false);

const selectedAccountingPartnerId = ref(0);
const accountingPartners = ref<AccountingPartner[]>([]);

const selectedIssuerId = ref(0);
const issuers = ref<Issuer[]>([]);


const cardNumber = ref<number>();
const cardErrorMessage = ref<string | string[]>("");

const snackbarRef = ref(null);
const {
  snackbarStatus,
  snackbarText,
  showSnackbar
} = useSnackbar(snackbarRef);

const onHandleFetchError = ({error}: {
  error: any
}) => {
  emits('onFetchError', {error})
}

const canResetUser = computed(() => hasEditRights
  && unref(cardNumber) && !unref(isValidationBusy)
  && unref(userValid));

const canResetVendor = computed(() => hasEditRights
  && unref(selectedAccountingPartnerId)
  && unref(vendorValid));

const canResetIssuer = computed(() => hasEditRights
  && unref(selectedIssuerId)
  && unref(issuerValid));

const onHandleResetPassword = async (group: 'customer' | 'user' | 'issuer', id: number | undefined) => {
  if (!id) {
    logError("Id is undefined or zero");
    return;
  }

  const {error} = await resetPassword(group, id);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  showSnackbar({ status: 'success', statusText: [t('success_message')] })
}

const loadAccountingPartners = async () => {
  const {response, error} = await fetchAccountingPartners();

  if (error) {
    onHandleFetchError({error});
    return;
  }

  accountingPartners.value = response ?? [];
}

const loadIssuers = async () => {

  const fetchArgs: FetchIssuersParam = {
    sortColumn: DEFAULT_SORT_COLUMN_FETCH_ISSUERS
  };

  if(issuerSearchInput.value) fetchArgs.companyName = issuerSearchInput.value;

  const {response, error} = await fetchIssuers(fetchArgs);
  if (error) {
    onHandleFetchError({error});
    return;
  }

  issuers.value = response ?? [];
}

watch(cardNumber, debounce(() => {
  if(!cardNumber.value){
    cardErrorMessage.value = [];
    return;
  }

  isValidationBusy.value = true;
  cardExist(unref(cardNumber)).then(result => {
    cardErrorMessage.value = result ? "" : t('rule_user_not_exist');
    userValid.value = result;
  }).catch(() => {
    userValid.value = false;
    cardErrorMessage.value = t('rule_could_not_check');
  }).finally(() => {
    isValidationBusy.value = false;
  });
}))

const validate = () => {
  vendorForm.value?.validate();
  issuerForm.value?.validate();
}

const onHandleSelectionChange = () => {
  const issuerId = issuerComboSelection.value?.Id; //  issuerComboSelection.value could be something else than an Issuer (string companyname without results)
  validate();

  if (!issuerId || !(issuerComboSelection.value instanceof Issuer)) {
    selectedIssuerId.value = 0;
    return;
  }
  ignoreIssuerSearchInputChange = true; // avoid a reload of the issuer list when the issuer is selected from the combobox

  selectedIssuerId.value = issuerId;
}


watch(() => issuerSearchInput.value, debounce(async () => {
  if(ignoreIssuerSearchInputChange) {
    ignoreIssuerSearchInputChange = false;
    return;
  }
  await loadIssuers();
}));

onMounted(() => validate())


return { __sfc: true,t, hasEditRights, emits, issuerComboSelection, issuerSearchInput, ignoreIssuerSearchInputChange, tabs, vendorForm, userForm, issuerForm, isValidationBusy, vendorValid, issuerValid, userValid, selectedAccountingPartnerId, accountingPartners, selectedIssuerId, issuers, cardNumber, cardErrorMessage, snackbarRef, snackbarStatus, snackbarText, showSnackbar, onHandleFetchError, canResetUser, canResetVendor, canResetIssuer, onHandleResetPassword, loadAccountingPartners, loadIssuers, validate, onHandleSelectionChange, FloatingActionButton, InfoSnackbar }
}

})